import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign, Fuel, ParkingCircle, Heart, ChevronDown, ChevronUp, TrendingUp,
} from "lucide-react";

export default function OutOfPocketEstimator({ outOfPocket, totalSavings, themeColor }) {
  const [expanded, setExpanded] = useState(false);
  const [parking, setParking] = useState(0);
  const [tips, setTips] = useState(0);

  useEffect(() => {
    if (outOfPocket) {
      setParking(outOfPocket.parking || 0);
      setTips(outOfPocket.tips || 0);
    }
  }, [outOfPocket]);

  if (!outOfPocket) return null;

  const gas = outOfPocket.gas || 0;
  const total = Math.round((gas + parking + tips) * 100) / 100;
  const netSavings = Math.round((totalSavings - total) * 100) / 100;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="out-of-pocket">
      <button onClick={() => setExpanded(!expanded)} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition" data-testid="oop-toggle">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: themeColor ? `${themeColor}18` : '#ecfdf5' }}>
          <DollarSign className="w-4 h-4" style={{ color: themeColor || '#10b981' }} />
        </div>
        <div className="flex-1 text-left">
          <p className="text-xs font-bold text-slate-800">Budget Breakdown</p>
          <p className="text-[10px] text-slate-400">~${total} out-of-pocket · <span className="font-bold" style={{ color: '#10b981' }}>${netSavings} net savings</span></p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 space-y-3">
              {/* Visual bar */}
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-slate-500">Deals value</span>
                  <span className="text-sm font-black text-emerald-600">${totalSavings}</span>
                </div>
                <div className="h-4 bg-slate-200 rounded-full overflow-hidden flex">
                  <div className="bg-emerald-400 h-full rounded-l-full" style={{ width: `${Math.min((netSavings / totalSavings) * 100, 100)}%` }} />
                  {total > 0 && <div className="bg-red-300 h-full" style={{ width: `${Math.min((total / totalSavings) * 100, 50)}%` }} />}
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[9px] text-emerald-600 font-bold">You keep: ${netSavings}</span>
                  <span className="text-[9px] text-red-400">Costs: ${total}</span>
                </div>
              </div>

              {/* Line items */}
              <div className="space-y-1.5">
                <CostRow icon={Fuel} label="Gas (estimated)" amount={gas} color="text-amber-500" />
                <EditableCostRow
                  icon={ParkingCircle}
                  label="Parking"
                  value={parking}
                  onChange={setParking}
                  color="text-sky-500"
                />
                <EditableCostRow
                  icon={Heart}
                  label="Tips"
                  value={tips}
                  onChange={setTips}
                  color="text-pink-500"
                />
              </div>

              <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs font-bold text-slate-700">Net birthday savings</span>
                </div>
                <span className="text-lg font-black text-emerald-600" data-testid="oop-net-savings">${netSavings}</span>
              </div>

              <p className="text-[8px] text-slate-300 text-center">Estimates based on ~$4/gal, 25 mpg avg. Edit parking & tips to match your area.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CostRow({ icon: Icon, label, amount, color }) {
  return (
    <div className="flex items-center gap-2.5 py-1">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-slate-600 flex-1">{label}</span>
      <span className="text-xs font-bold text-slate-700">${amount}</span>
    </div>
  );
}

function EditableCostRow({ icon: Icon, label, value, onChange, color }) {
  return (
    <div className="flex items-center gap-2.5 py-1">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-slate-600 flex-1">{label}</span>
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-400">$</span>
        <input
          type="number"
          min={0}
          step={1}
          value={value}
          onChange={e => onChange(Math.max(0, parseFloat(e.target.value) || 0))}
          className="w-14 text-xs font-bold text-slate-700 text-right bg-slate-50 border border-slate-200 rounded-md px-1.5 py-1 focus:outline-none focus:border-sky-300 focus:ring-1 focus:ring-sky-100"
          data-testid={`oop-edit-${label.toLowerCase()}`}
        />
      </div>
    </div>
  );
}
